export type BitDepth = 16 | 24 | 32;

/** Encodes an AudioBuffer as a RIFF/WAVE blob. 32-bit is IEEE float. */
export function encodeWav(buffer: AudioBuffer, depth: BitDepth = 24): Blob {
  const channels = buffer.numberOfChannels;
  const frames = buffer.length;
  const bytesPerSample = depth / 8;
  const blockAlign = channels * bytesPerSample;
  const dataSize = frames * blockAlign;
  const isFloat = depth === 32;

  const header = new ArrayBuffer(44);
  const view = new DataView(header);
  const w = (off: number, s: string) => {
    for (let i = 0; i < s.length; i++) view.setUint8(off + i, s.charCodeAt(i));
  };
  w(0, 'RIFF');
  view.setUint32(4, 36 + dataSize, true);
  w(8, 'WAVE');
  w(12, 'fmt ');
  view.setUint32(16, 16, true);
  view.setUint16(20, isFloat ? 3 : 1, true);
  view.setUint16(22, channels, true);
  view.setUint32(24, buffer.sampleRate, true);
  view.setUint32(28, buffer.sampleRate * blockAlign, true);
  view.setUint16(32, blockAlign, true);
  view.setUint16(34, depth, true);
  w(36, 'data');
  view.setUint32(40, dataSize, true);

  const data = new ArrayBuffer(dataSize);
  const dv = new DataView(data);
  const chans: Float32Array[] = [];
  for (let c = 0; c < channels; c++) chans.push(buffer.getChannelData(c));

  let off = 0;
  for (let i = 0; i < frames; i++) {
    for (let c = 0; c < channels; c++) {
      const s = Math.max(-1, Math.min(1, chans[c][i]));
      if (isFloat) {
        dv.setFloat32(off, s, true);
      } else if (depth === 16) {
        dv.setInt16(off, s < 0 ? s * 0x8000 : s * 0x7fff, true);
      } else {
        const v = Math.round(s < 0 ? s * 0x800000 : s * 0x7fffff);
        dv.setUint8(off, v & 0xff);
        dv.setUint8(off + 1, (v >> 8) & 0xff);
        dv.setUint8(off + 2, (v >> 16) & 0xff);
      }
      off += bytesPerSample;
    }
  }

  return new Blob([header, data], { type: 'audio/wav' });
}

export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 4000);
}

export function safeName(s: string): string {
  return s.replace(/[^a-z0-9\-_ ]/gi, '').trim().replace(/\s+/g, '-') || 'happy-chopper';
}
